import { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import type { Intake, IntakeItem, UserData } from '../../types'
import { lookupFood } from '../../utils/api'
import { uniqueIntakesByName } from '../../utils/entries'
import { intakeTotal, itemsTotal } from '../../utils/intake'
import { nowDatetime, DATETIME_FORMAT } from '../../utils/calc'
import Modal from '../layout/Modal'
import NutritionDetails from './NutritionDetails'

export type FoodMode = 'new' | 'edit' | 'copy'

interface FoodFormProps {
  isOpen: boolean
  mode: FoodMode
  seed: Intake | null
  index: number | null
  data: UserData
  onClose: () => void
  onSave: (intake: Intake, index: number | null) => void
}

type Stage = 'input' | 'loading' | 'confirm' | 'manual'
type KeptItem = IntakeItem & { _keep: boolean }

const toLocalInput = (s: string) => (s ? moment(s, DATETIME_FORMAT).format('YYYY-MM-DDTHH:mm') : '')
const fromLocalInput = (s: string) => (s ? moment(s, 'YYYY-MM-DDTHH:mm').format(DATETIME_FORMAT) : '')

export default function FoodForm({ isOpen, mode, seed, index, data, onClose, onSave }: FoodFormProps) {
  const [stage, setStage] = useState<Stage>('input')
  const [query, setQuery] = useState('')
  const [name, setName] = useState('')
  const [items, setItems] = useState<KeptItem[]>([])
  const [servings, setServings] = useState('1') // string-backed so decimals (e.g. "1.5") type cleanly
  const [datetime, setDatetime] = useState(nowDatetime())
  const [note, setNote] = useState('')
  const [showDetails, setShowDetails] = useState(false)

  // manual fields
  const [mName, setMName] = useState('')
  const [mCalories, setMCalories] = useState('')
  const [mProtein, setMProtein] = useState('')
  const [mCarbs, setMCarbs] = useState('')
  const [mFat, setMFat] = useState('')

  useEffect(() => {
    if (!isOpen) return
    setNote('')
    if (mode === 'new') {
      setStage('input')
      setQuery('')
      setName('')
      setItems([])
      setServings('1')
      setDatetime(nowDatetime())
      setMName('')
      setMCalories('')
      setMProtein('')
      setMCarbs('')
      setMFat('')
      return
    }
    // edit / copy — preload from seed
    if (seed) {
      setQuery(seed.query || seed.name)
      setName(seed.name)
      setServings(String(seed.servings || 1))
      setDatetime(mode === 'copy' ? nowDatetime() : seed.datetime)
      if (seed.manual) {
        const it = seed.items[0]
        setMName(seed.name)
        setMCalories(it?.calories != null ? String(it.calories) : '')
        setMProtein(it?.protein != null ? String(it.protein) : '')
        setMCarbs(it?.carbs != null ? String(it.carbs) : '')
        setMFat(it?.fat != null ? String(it.fat) : '')
        setStage('manual')
      } else {
        setItems(seed.items.map((it) => ({ ...it, _keep: true })))
        setStage('confirm')
      }
    }
  }, [isOpen, mode, seed])

  const runLookup = async () => {
    const q = query.trim()
    if (q.length < 2) {
      setNote('Enter at least 2 characters.')
      return
    }
    setStage('loading')
    const r = await lookupFood(q)
    if (r.ok) {
      setName(r.name)
      setItems(r.items.map((it) => ({ ...it, _keep: true })))
      setServings('1')
      setDatetime(nowDatetime())
      setStage('confirm')
    } else {
      setMName(q)
      setMCalories('')
      setMProtein('')
      setMCarbs('')
      setMFat('')
      setNote(
        r.reason === 'unconfigured'
          ? 'Automatic lookup is off — enter the details yourself.'
          : `${r.message} Enter the details yourself.`,
      )
      setStage('manual')
    }
  }

  // Type-ahead over previously-logged food, so a repeat meal skips the API lookup.
  const history = useMemo(() => uniqueIntakesByName(data), [data])
  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (stage !== 'input' || q.length < 1) return []
    return history.filter((it) => `${it.name} ${it.query}`.toLowerCase().includes(q)).slice(0, 6)
  }, [history, query, stage])

  // Re-log a past entry directly (no lookup): preload it like a fresh copy.
  const loadFromHistory = (it: Intake) => {
    setNote('')
    setName(it.name)
    setQuery(it.query || it.name)
    setDatetime(nowDatetime())
    if (it.manual) {
      const m = it.items[0]
      setMName(it.name)
      setMCalories(m?.calories != null ? String(m.calories) : '')
      setMProtein(m?.protein != null ? String(m.protein) : '')
      setMCarbs(m?.carbs != null ? String(m.carbs) : '')
      setMFat(m?.fat != null ? String(m.fat) : '')
      setStage('manual')
    } else {
      setItems(it.items.map((x) => ({ ...x, _keep: true })))
      setServings(String(it.servings || 1))
      setStage('confirm')
    }
  }

  const kept = items.filter((it) => it._keep)
  const total = itemsTotal(kept, Number(servings) || 1)

  const saveConfirm = () => {
    if (kept.length === 0) {
      setNote('Keep at least one item.')
      return
    }
    const intake: Intake = {
      datetime,
      query: query.trim(),
      name: name.trim() || query.trim(),
      servings: Number(servings) || 1,
      items: kept.map(({ _keep, ...it }) => {
        void _keep
        return it
      }),
    }
    onSave(intake, mode === 'edit' ? index : null)
  }

  const saveManual = () => {
    const cal = Number(mCalories)
    if (!mName.trim()) {
      setNote('Enter a name.')
      return
    }
    if (!(cal > 0)) {
      setNote('Enter the calories.')
      return
    }
    const item: IntakeItem = { name: mName.trim(), calories: cal }
    if (Number(mProtein) > 0) item.protein = Number(mProtein)
    if (Number(mCarbs) > 0) item.carbs = Number(mCarbs)
    if (Number(mFat) > 0) item.fat = Number(mFat)
    const intake: Intake = {
      datetime,
      query: query.trim() || mName.trim(),
      name: mName.trim(),
      servings: 1,
      items: [item],
      manual: true,
    }
    onSave(intake, mode === 'edit' ? index : null)
  }

  const title =
    mode === 'edit' ? 'Edit Food & Drink' : 'Log Food & Drink'

  const footer =
    stage === 'input' ? (
      <>
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={runLookup}>
          Look up
        </button>
      </>
    ) : stage === 'confirm' ? (
      <>
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={saveConfirm}>
          Log it
        </button>
      </>
    ) : stage === 'manual' ? (
      <>
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={saveManual}>
          Log it
        </button>
      </>
    ) : undefined

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="lg" footer={footer}>
      {note && <p className="text-sm text-[var(--accent-orange)] mb-3">{note}</p>}

      {stage === 'input' && (
        <div>
          <label className="block text-sm mb-1 text-[var(--text-secondary)]">
            What did you eat or drink?
          </label>
          <input
            className="w-full"
            autoFocus
            placeholder="e.g. Jumbo Ogalo Meal Deal"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') runLookup()
            }}
          />
          <p className="text-xs text-[var(--text-secondary)] mt-2">
            Describe it in plain language — we'll work out the nutrition for you to confirm.
          </p>

          {suggestions.length > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wide text-[var(--text-secondary)] mb-1">From your history</div>
              <ul className="divide-y divide-[var(--border)] rounded-lg border border-[var(--border)]">
                {suggestions.map((it, i) => {
                  const t = intakeTotal(it)
                  return (
                    <li key={i}>
                      <button
                        type="button"
                        onClick={() => loadFromHistory(it)}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-[var(--bg-card)]"
                      >
                        <span className="min-w-0">
                          <span className="block truncate text-[var(--text-primary)]">{it.name}</span>
                          <span className="block text-xs text-[var(--text-secondary)]">
                            {it.items.length} item{it.items.length === 1 ? '' : 's'}
                            {it.manual ? ' · manual' : ''}
                          </span>
                        </span>
                        <span className="shrink-0 text-sm text-[var(--accent-teal)]">{Math.round(t.calories)} kcal</span>
                      </button>
                    </li>
                  )
                })}
              </ul>
              <p className="text-xs text-[var(--text-secondary)] mt-1">Pick one to reuse it without a new lookup.</p>
            </div>
          )}
        </div>
      )}

      {stage === 'loading' && (
        <div className="flex flex-col items-center justify-center py-10 gap-3">
          <div className="spinner" />
          <p className="text-sm text-[var(--text-secondary)]">Working out the nutrition…</p>
        </div>
      )}

      {stage === 'confirm' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm mb-1 text-[var(--text-secondary)]">Name</label>
            <input className="w-full" value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div>
            <div className="text-sm text-[var(--text-secondary)] mb-1">Items (untick anything you didn't have)</div>
            <ul className="divide-y divide-[var(--border)] rounded-lg border border-[var(--border)]">
              {items.map((it, i) => (
                <li key={i} className="flex items-center gap-3 px-3 py-2">
                  <input
                    type="checkbox"
                    className="w-auto"
                    checked={it._keep}
                    onChange={(e) =>
                      setItems((prev) =>
                        prev.map((p, pi) => (pi === i ? { ...p, _keep: e.target.checked } : p)),
                      )
                    }
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-[var(--text-primary)]">
                      {it.name}
                      {it.quantity ? <span className="text-[var(--text-secondary)]"> · {it.quantity}</span> : null}
                    </div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      {Math.round(it.calories)} kcal
                      {it.protein != null ? ` · P ${it.protein}g` : ''}
                      {it.carbs != null ? ` · C ${it.carbs}g` : ''}
                      {it.fat != null ? ` · F ${it.fat}g` : ''}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1 text-[var(--text-secondary)]">Servings</label>
              <input
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                className="w-full"
                value={servings}
                onChange={(e) => setServings(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm mb-1 text-[var(--text-secondary)]">Date/Time</label>
              <input
                type="datetime-local"
                className="w-full"
                value={toLocalInput(datetime)}
                onChange={(e) => setDatetime(fromLocalInput(e.target.value))}
              />
            </div>
          </div>

          <div className="card">
            <div className="text-center">
              <div className="text-3xl font-bold text-[var(--accent-teal)]">
                {Math.round(total.calories)} <span className="text-base font-normal">kcal</span>
              </div>
              <div className="text-sm text-[var(--text-secondary)] mt-1">
                Protein {Math.round(total.protein || 0)}g · Carbs {Math.round(total.carbs || 0)}g · Fat{' '}
                {Math.round(total.fat || 0)}g
              </div>
            </div>
            <div className="mt-3 border-t border-[var(--border)] pt-2">
              <button
                className="text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                onClick={() => setShowDetails((s) => !s)}
                aria-expanded={showDetails}
              >
                Full nutrition {showDetails ? '▴' : '▾'}
              </button>
              {showDetails && (
                <div className="mt-2">
                  <NutritionDetails panel={total} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {stage === 'manual' && (
        <div className="space-y-3">
          <div>
            <label className="block text-sm mb-1 text-[var(--text-secondary)]">Name</label>
            <input className="w-full" value={mName} onChange={(e) => setMName(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm mb-1 text-[var(--text-secondary)]">Calories (kcal)</label>
              <input className="w-full" type="number" inputMode="decimal" value={mCalories} onChange={(e) => setMCalories(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm mb-1 text-[var(--text-secondary)]">Date/Time</label>
              <input
                type="datetime-local"
                className="w-full"
                value={toLocalInput(datetime)}
                onChange={(e) => setDatetime(fromLocalInput(e.target.value))}
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm mb-1 text-[var(--text-secondary)]">Protein (g)</label>
              <input className="w-full" type="number" inputMode="decimal" value={mProtein} onChange={(e) => setMProtein(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm mb-1 text-[var(--text-secondary)]">Carbs (g)</label>
              <input className="w-full" type="number" inputMode="decimal" value={mCarbs} onChange={(e) => setMCarbs(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm mb-1 text-[var(--text-secondary)]">Fat (g)</label>
              <input className="w-full" type="number" inputMode="decimal" value={mFat} onChange={(e) => setMFat(e.target.value)} />
            </div>
          </div>
          <p className="text-xs text-[var(--text-secondary)]">Calories required; macros optional.</p>
        </div>
      )}
    </Modal>
  )
}
